// CNC 파트너(제조사) 화면 공용 — 상태 표기·날짜 도우미·스타일 (2026-10-23, UI 구조 단계)
import React, { useEffect, useState } from 'react';
import { MF_KIND_LABEL, MF_KIND_COLOR, teethArray } from '../../services/outsourceMfApi';

// 상태 3단계 — 나중에 서버 상태(opened / in_production / shipped)와 짝을 맞춘다
export const STATUS = {
    received: { label: '접수', fg: '#1d4ed8', bg: '#dbeafe' },
    milling: { label: '밀링 중', fg: '#b45309', bg: '#fef3c7' },
    shipped: { label: '출고', fg: '#047857', bg: '#d1fae5' },
};
export const STATUS_ORDER = ['received', 'milling', 'shipped'];

export const JIG = {
    ready: { label: '지그', fg: '#0f766e' },
    pending: { label: '생성 중', fg: '#64748b' },
    failed: { label: '실패', fg: '#dc2626' },
};

const DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// 납기까지 남은 일수(오늘 = 0, 지남 = 음수)
export const daysLeft = (due) => Math.round((startOfDay(due) - startOfDay(new Date())) / DAY);
export const ddayText = (due) => {
    const n = daysLeft(due);
    if (n === 0) return 'D-day';
    return n > 0 ? `D-${n}` : `D+${-n}`;
};
export const isDueSoon = (c) => c.status !== 'shipped' && daysLeft(c.dueAt) <= 1;
export const isToday = (d) => startOfDay(d).getTime() === startOfDay(new Date()).getTime();
// 출고 미확인 = 밀링 중으로 3일 넘게 머문 건(명세서 수량과 어긋나지 않게 매일 알림)
export const isUnshipped = (c) => c.status === 'milling' && (Date.now() - c.receivedAt.getTime()) > 3 * DAY;

export const fmtMd = (d) => (d ? `${d.getMonth() + 1}/${d.getDate()}` : '—');
export const fmtMdHm = (d) => (d ? `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '—');
export const won = (n) => (n == null ? '—' : `${Number(n).toLocaleString('ko-KR')}원`);

// 환자 이름 표시(대표 10-23 「케이스는 환자 이름으로」 — 화면 표시만, 각인·파일명·명세서는 번호)
//   CNC: 실명 스위치(outsourceMfCncName) ON 전에는 숨김. 그 외: 서버 rules 기한(확인·제작 중 후 30일·출고 후 같은 기한) 안에서만 — 샘플은 보임 처리
export const patientNameOf = (c, cncNameOn) => {
    if (!c || !c.patientName) return null;
    if (c.kind === 'cnc' && !cncNameOn) return null;
    return c.patientName;
};

// 파일명 규칙(덴클 기본 고정): CNC = 케이스ID_치식_시스템_abutment.stl / _jig.stl, 그 외 = 케이스ID_치식_품목.stl
const teethKey = (c) => teethArray(c.teeth).join('-');
export const fileNameOf = (c, part) => {
    const d = c.detail || {};
    const tail = c.kind === 'cnc' ? `${(d.system || '').replace(/\s+/g, '')}_${part}` : (d.item || MF_KIND_LABEL[c.kind] || 'file').replace(/\s+/g, '');
    return `${c.caseNo}_${teethKey(c)}_${tail}.stl`;
};

// 종류 칩 — 기공소 외주 의뢰서와 같은 이름·색(outsourceMfApi)
export const KIND_ORDER = ['cnc', 'metal', 'zirconia', 'jig', 'general', 'etc'];   // general 은 옛 주문 표시용(10-25)
export function KindChip({ kind }) {
    const c = MF_KIND_COLOR[kind] || MF_KIND_COLOR.etc;
    return <span style={{ ...ui.badge, color: c.fg, backgroundColor: c.bg }}>{MF_KIND_LABEL[kind] || kind}</span>;
}

// 기공소가 적은 상세를 [라벨, 값] 목록으로 — 패널·툴팁용(빈 값은 뺀다)
export function detailRows(c) {
    const d = c.detail || {};
    const rows = [
        ['품목', d.item], ['재료', d.material],
        ['임플란트 시스템', d.system], ['연결', d.connection], ['플랫폼', d.platform], ['직경', d.diameter ? `Ø${d.diameter}` : ''],
        ['쉐이드', d.shade], ['후가공', d.postProcess],
        ['아노다이징', d.anodizing ? (d.anodizingColor || '예') : ''],
        ['수량', d.qty ? `${d.qty}개` : ''],
    ];
    return rows.filter(([, v]) => v);
}

export function useIsMobile(bp = 768) {
    const get = () => (typeof window !== 'undefined' ? window.innerWidth < bp : false);
    const [mobile, setMobile] = useState(get);
    useEffect(() => {
        const on = () => setMobile(get());
        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return mobile;
}

export function StatusBadge({ status }) {
    const s = STATUS[status] || STATUS.received;
    return <span style={{ ...ui.badge, color: s.fg, backgroundColor: s.bg }}>{s.label}</span>;
}

// UI 구조 단계 안내 — 샘플 데이터임을 화면에 항상 표시
export function SampleNotice() {
    return (
        <div style={ui.sampleNotice}>
            {'미리보기 화면입니다 — 아래 업체·케이스·금액·승인 상태는 모두 예시이며 실제 데이터가 아닙니다.'}
        </div>
    );
}

export const ui = {
    body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
    bodyMobile: { padding: 12, display: 'flex', flexDirection: 'column', gap: 12 },
    card: { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 },
    cardTitle: { fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 },
    cardSub: { fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.5 },
    badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
    // 샘플 안내 — 실제 업체가 사실로 오해하지 않게 눈에 띄게(시뮬레이션 10-23 [상]2)
    sampleNotice: {
        padding: '10px 14px', borderRadius: 9, backgroundColor: '#fffbeb', border: '1px solid #fcd34d',
        color: '#92400e', fontSize: 13, fontWeight: 700,
    },
    // 툴바·입력
    toolBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    searchInput: {
        flex: '1 1 200px', minWidth: 0, maxWidth: 300, padding: '8px 12px',
        border: '1px solid #cbd5e1', borderRadius: 9, fontSize: 13, color: '#0f172a', outline: 'none',
    },
    select: {
        padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13,
        color: '#334155', backgroundColor: '#fff', cursor: 'pointer',
    },
    ghostBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
        border: '1px solid #cbd5e1', borderRadius: 8, backgroundColor: '#fff',
        color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    primaryBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
        border: 'none', borderRadius: 8, backgroundColor: '#6366f1',
        color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    // 테이블
    tableWrap: { overflowX: 'auto', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    td: { padding: '10px 12px', color: '#1e293b', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' },
    tdNum: { padding: '10px 12px', color: '#1e293b', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap' },
    muted: { fontSize: 12, color: '#94a3b8' },
    empty: {
        padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14,
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    },
    // 탭
    tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', overflowX: 'auto' },
    tab: {
        padding: '9px 14px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600,
        color: '#64748b', cursor: 'pointer', borderBottom: '2px solid transparent', whiteSpace: 'nowrap',
    },
    tabActive: { color: '#4338ca', borderBottom: '2px solid #6366f1' },
};
